import { useEffect } from "react";
import StaticPageLayout from "@/components/layout/StaticPageLayout";
import { Mail, MessageSquare, FileText, Clock } from "lucide-react";

const Help = () => {
  useEffect(() => {
    document.title = "Help & Support | Decision Sales OS";
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute("content", "Get help with Decision Sales OS. Access documentation, contact support, and find answers to common questions.");
    }
  }, []);

  return (
    <StaticPageLayout 
      title="Help & Support"
      description="We are here to help you get the most out of Decision Sales OS."
    >
      <section className="mb-12">
        <h2 className="text-xl font-semibold text-foreground mb-6">Contact Support</h2>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="p-6 rounded-lg border border-border bg-card">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
              <Mail className="w-5 h-5 text-primary" />
            </div>
            <h3 className="font-medium text-foreground mb-2">Email Support</h3>
            <p className="text-sm text-muted-foreground mb-3">
              For general inquiries and technical support requests.
            </p>
            <a 
              href="mailto:info@decisionsalesos.com" 
              className="text-sm text-primary hover:text-primary/80 transition-colors"
            >
              info@decisionsalesos.com
            </a>
          </div>
          <div className="p-6 rounded-lg border border-border bg-card">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
              <Clock className="w-5 h-5 text-primary" />
            </div>
            <h3 className="font-medium text-foreground mb-2">Response Time</h3>
            <p className="text-sm text-muted-foreground">
              We respond to all support requests within 24 hours during business days. 
              Priority support is available for Pro customers.
            </p>
          </div>
        </div>
      </section>

      <section className="mb-12">
        <h2 className="text-xl font-semibold text-foreground mb-6">Common Questions</h2>
        <div className="space-y-6">
          <div className="border-b border-border pb-6">
            <h3 className="font-medium text-foreground mb-2">How do I add new leads?</h3>
            <p className="text-sm text-muted-foreground">
              Navigate to the Leads section from the main menu. Click the "Add Lead" button to create 
              a new lead manually, or use the CSV import feature to add multiple leads at once.
            </p>
          </div>
          <div className="border-b border-border pb-6">
            <h3 className="font-medium text-foreground mb-2">How does the decision engine work?</h3>
            <p className="text-sm text-muted-foreground">
              The decision engine analyzes your leads based on temperature, last interaction date, 
              pipeline stage, and priority level. It then surfaces the most important actions for 
              your day, helping you focus on high-impact activities.
            </p>
          </div>
          <div className="border-b border-border pb-6">
            <h3 className="font-medium text-foreground mb-2">Can I invite team members?</h3>
            <p className="text-sm text-muted-foreground">
              Yes. Go to Settings, then Team to invite colleagues to your workspace. You can assign 
              roles and manage permissions from the same screen.
            </p>
          </div>
          <div className="border-b border-border pb-6">
            <h3 className="font-medium text-foreground mb-2">How do I track deal progress?</h3>
            <p className="text-sm text-muted-foreground">
              The Pipeline view shows all your deals organized by stage. Drag and drop deals between 
              stages to update their status, or click on any deal to view and edit its details.
            </p>
          </div>
          <div>
            <h3 className="font-medium text-foreground mb-2">Is my data secure?</h3>
            <p className="text-sm text-muted-foreground">
              Yes. We use industry-standard encryption for data in transit and at rest. All data is 
              stored securely and access is controlled through role-based permissions. See our 
              Privacy Policy for more details.
            </p>
          </div>
        </div>
      </section>

      <section className="mb-12">
        <h2 className="text-xl font-semibold text-foreground mb-6">Resources</h2>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="p-6 rounded-lg border border-border bg-card">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
              <FileText className="w-5 h-5 text-primary" />
            </div>
            <h3 className="font-medium text-foreground mb-2">Documentation</h3>
            <p className="text-sm text-muted-foreground">
              Comprehensive guides covering all features and workflows in Decision Sales OS.
            </p>
          </div>
          <div className="p-6 rounded-lg border border-border bg-card">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
              <MessageSquare className="w-5 h-5 text-primary" />
            </div>
            <h3 className="font-medium text-foreground mb-2">Feature Requests</h3>
            <p className="text-sm text-muted-foreground">
              Have an idea for improving Decision Sales OS? We welcome feedback from our users.
            </p>
          </div>
        </div>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-foreground mb-4">Need help with onboarding?</h2>
        <p className="text-muted-foreground leading-relaxed">
          For setup, billing, team invites, or Meta Lead Ads onboarding, contact us at{" "}
          <a 
            href="mailto:info@decisionsalesos.com" 
            className="text-primary hover:text-primary/80 transition-colors"
          >
            info@decisionsalesos.com
          </a>
        </p>
      </section>
    </StaticPageLayout>
  );
};

export default Help;
